<template>
  <div class="onboarding-step">
    <h2 class="step-title">Create your first writer</h2>
    <p class="step-description">
      A writer defines the voice and style for your content. You can create more writers later.
    </p>

    <div class="form-group">
      <label class="form-label required">Writer name</label>
      <input
        v-model="localData.name"
        type="text"
        class="form-input"
        placeholder="e.g., Blog Writer, Technical Writer"
      />
    </div>

    <!-- AI Analysis Section -->
    <div class="ai-section">
      <div class="ai-section-header">
        <div class="ai-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"/>
          </svg>
        </div>
        <div class="ai-section-text">
          <h3 class="ai-section-title">Auto-fill with AI</h3>
          <p class="ai-section-subtitle">Paste a sample article URL and we'll extract the writing style</p>
        </div>
      </div>
      <div class="ai-input-group">
        <input
          v-model="referenceUrl"
          type="url"
          class="ai-input"
          placeholder="https://example.com/blog-post"
          @keydown.enter="analyzeWithAI"
        />
        <button
          type="button"
          class="btn btn-primary ai-analyze-btn"
          :disabled="!referenceUrl || isAnalyzing"
          @click="analyzeWithAI"
        >
          <LoadingSpinner v-if="isAnalyzing" size="sm" />
          <template v-else>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="btn-icon">
              <path d="M7.156 2.344a.75.75 0 01.594.75v.312c0 1.25.455 2.435 1.28 3.356l.13.15a.75.75 0 01-1.12.992l-.13-.15a6.257 6.257 0 01-1.66-4.348V3.094a.75.75 0 01.906-.75zm5.688 0a.75.75 0 01.906.75v.312a6.257 6.257 0 01-1.66 4.348l-.13.15a.75.75 0 01-1.12-.992l.13-.15a4.757 4.757 0 001.28-3.356V3.094a.75.75 0 01.594-.75zM10 6.5a3.5 3.5 0 100 7 3.5 3.5 0 000-7zm0 1.5a2 2 0 110 4 2 2 0 010-4zm-7.5 4a.75.75 0 01.75-.75h.312a4.757 4.757 0 003.356-1.28l.15-.13a.75.75 0 01.992 1.12l-.15.13a6.257 6.257 0 01-4.348 1.66H3.25a.75.75 0 01-.75-.906zm14.156.156a.75.75 0 01.75.75v.312a.75.75 0 01-.75.75h-.312a6.257 6.257 0 01-4.348-1.66l-.15-.13a.75.75 0 01.992-1.12l.15.13a4.757 4.757 0 003.356 1.28h.312a.75.75 0 01.75.438z"/>
            </svg>
            Analyze
          </template>
        </button>
      </div>
    </div>

    <!-- Divider -->
    <div class="divider">
      <span class="divider-text">or fill in manually</span>
    </div>

    <!-- Manual Input Section -->
    <div class="manual-section">
      <div class="form-group">
        <label class="form-label">Tone of voice</label>
        <textarea
          v-model="localData.toneOfVoice"
          class="form-textarea"
          rows="3"
          placeholder="e.g., Professional but approachable, conversational, authoritative..."
        ></textarea>
      </div>

      <div class="form-group">
        <label class="form-label">Style guide</label>
        <textarea
          v-model="localData.styleGuide"
          class="form-textarea"
          rows="3"
          placeholder="e.g., Use active voice, short paragraphs, include examples..."
        ></textarea>
      </div>

      <div class="form-group">
        <label class="form-label">Speech patterns (optional)</label>
        <textarea
          v-model="localData.speechPatternGuidelines"
          class="form-textarea"
          rows="2"
          placeholder="e.g., Avoid jargon, use 'we' instead of 'I', include rhetorical questions..."
        ></textarea>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useStore } from 'vuex'
import { aiAPI } from '@/api'
import { handleError } from '@/utils/errorHandler'
import LoadingSpinner from '@/components/common/LoadingSpinner.vue'

interface StepData {
  name: string
  toneOfVoice: string
  styleGuide: string
  speechPatternGuidelines: string
}

const props = defineProps<{
  initialData: StepData
}>()

const emit = defineEmits<{
  (e: 'update', data: StepData): void
}>()

const store = useStore()
const localData = ref<StepData>({ ...props.initialData })
const referenceUrl = ref('')
const isAnalyzing = ref(false)

watch(localData, (newData) => {
  emit('update', { ...newData })
}, { deep: true })

const analyzeWithAI = async () => {
  if (!referenceUrl.value || isAnalyzing.value) return

  isAnalyzing.value = true
  try {
    const result = await aiAPI.analyzeWriterUrl(referenceUrl.value)
    if (result.tone_of_voice) {
      localData.value.toneOfVoice = result.tone_of_voice
    }
    if (result.style_guide) {
      localData.value.styleGuide = result.style_guide
    }
    if (result.speech_patterns) {
      localData.value.speechPatternGuidelines = result.speech_patterns
    }
  } catch (error) {
    handleError(error, store)
  } finally {
    isAnalyzing.value = false
  }
}
</script>

<style scoped>
.onboarding-step {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.step-title {
  font-size: var(--text-xl);
  font-weight: var(--font-bold);
  color: var(--text-primary);
  margin: 0;
}

.step-description {
  font-size: var(--text-base);
  color: var(--text-secondary);
  margin: 0;
  line-height: var(--leading-relaxed);
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.form-label {
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  color: var(--text-secondary);
}

.form-label.required::after {
  content: ' *';
  color: var(--color-error-400);
}

.form-input,
.form-textarea {
  padding: var(--space-3);
  font-size: var(--text-base);
  font-family: inherit;
  background: var(--surface-input);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  transition: all var(--transition-fast);
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: var(--color-primary-500);
  box-shadow: var(--shadow-focus);
}

.form-input::placeholder,
.form-textarea::placeholder {
  color: var(--text-placeholder);
}

.form-textarea {
  resize: vertical;
  min-height: 60px;
}

/* AI Section */
.ai-section {
  background: linear-gradient(135deg, rgba(26, 115, 232, 0.08) 0%, rgba(66, 133, 244, 0.04) 100%);
  border: 1px solid rgba(26, 115, 232, 0.2);
  border-radius: var(--radius-xl);
  padding: var(--space-5);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.ai-section-header {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
}

.ai-icon {
  width: 40px;
  height: 40px;
  background: var(--gradient-primary);
  border-radius: var(--radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ai-icon svg {
  width: 22px;
  height: 22px;
  color: white;
}

.ai-section-text {
  flex: 1;
}

.ai-section-title {
  font-size: var(--text-base);
  font-weight: var(--font-semibold);
  color: var(--text-primary);
  margin: 0 0 var(--space-1) 0;
}

.ai-section-subtitle {
  font-size: var(--text-sm);
  color: var(--text-secondary);
  margin: 0;
}

.ai-input-group {
  display: flex;
  gap: var(--space-2);
}

.ai-input {
  flex: 1;
  padding: var(--space-3) var(--space-4);
  font-size: var(--text-base);
  font-family: inherit;
  background: var(--surface-input);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  color: var(--text-primary);
  transition: all var(--transition-fast);
}

.ai-input:focus {
  outline: none;
  border-color: var(--color-primary-500);
  box-shadow: var(--shadow-focus);
}

.ai-input::placeholder {
  color: var(--text-placeholder);
}

.ai-analyze-btn {
  flex-shrink: 0;
  padding: var(--space-3) var(--space-5);
}

.ai-analyze-btn .btn-icon {
  width: 18px;
  height: 18px;
}

/* Divider */
.divider {
  display: flex;
  align-items: center;
  gap: var(--space-4);
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--border-default);
}

.divider-text {
  font-size: var(--text-sm);
  color: var(--text-tertiary);
  text-transform: lowercase;
}

/* Manual Section */
.manual-section {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  opacity: 0.9;
}
</style>
